import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Calendar } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BibleVerse from "@/components/BibleVerse";
import { translateScriptureReference } from "@/utils/bibleTranslations";

interface YouTubeVideo {
  id: string;
  title: string;
  youtube_url: string;
  description: string | null;
  display_order: number;
  published: boolean;
  created_at: string;
}

interface VideoTranslation {
  title: string;
  description: string | null;
}

export default function YouTubeVideos() {
  const { t, language } = useLanguage();
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [translations, setTranslations] = useState<Record<string, VideoTranslation>>({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<YouTubeVideo | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>("all");
  const [formData, setFormData] = useState({
    title: "",
    youtube_url: "",
    description: "",
    published: false,
  });

  useEffect(() => {
    fetchVideos();
  }, [language]);

  const fetchVideos = async () => {
    try {
      const { data: videosData, error: videosError } = await supabase
        .from("youtube_videos")
        .select("*")
        .order("display_order", { ascending: true });

      if (videosError) throw videosError;
      setVideos(videosData || []);

      // Fetch translations for the current language
      if (videosData && videosData.length > 0) {
        const { data: translationsDataResult, error: translationsError } = await supabase
          .from("youtube_videos_translations")
          .select("video_id, title, description")
          .eq("language", language)
          .in("video_id", videosData.map(v => v.id));

        if (!translationsError) {
          const translationsMap: Record<string, VideoTranslation> = {};
          if (translationsDataResult && translationsDataResult.length > 0) {
            translationsDataResult.forEach(t => {
              translationsMap[t.video_id] = {
                title: t.title,
                description: t.description
              };
            });
          }
          setTranslations(translationsMap);
        }
      } else {
        setTranslations({});
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast.error("Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  const getVideoContent = (video: YouTubeVideo) => {
    const translation = translations[video.id];
    return {
      title: translation?.title || video.title,
      description: translation?.description || video.description
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingVideo) {
        // Update video
        const { error } = await supabase
          .from("youtube_videos")
          .update({
            title: formData.title,
            youtube_url: formData.youtube_url,
            description: formData.description,
            published: formData.published,
          })
          .eq("id", editingVideo.id);

        if (error) throw error;
        toast.success(t("youtube.admin.updatedsuccess"));
      } else {
        // Insert video
        const { error } = await supabase
          .from("youtube_videos")
          .insert({
            title: formData.title,
            youtube_url: formData.youtube_url,
            description: formData.description,
            published: formData.published,
            display_order: videos.length,
          });

        if (error) throw error;
        toast.success(t("youtube.admin.addedsuccess"));
      }

      setDialogOpen(false);
      resetForm();
      fetchVideos();
    } catch (error) {
      console.error("Error saving video:", error);
      toast.error(t("youtube.admin.saveerror"));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t("youtube.admin.deleteconfirm"))) return;

    try {
      const { error } = await supabase
        .from("youtube_videos")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success(t("youtube.admin.deletedsuccess"));
      fetchVideos();
    } catch (error) {
      console.error("Error deleting video:", error);
      toast.error(t("youtube.admin.deleteerror"));
    }
  };

  const handleEdit = (video: YouTubeVideo) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      youtube_url: video.youtube_url,
      description: video.description || "",
      published: video.published,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      youtube_url: "",
      description: "",
      published: false,
    });
    setEditingVideo(null);
  };

  const getYouTubeVideoId = (url: string): string => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : '';
  };

  const parseScriptureReferences = (description: string | null): string[] => {
    if (!description) return [];
    // Split by comma, semicolon, or newline, trim whitespace, remove bullet points and filter empty strings
    const refs = description.split(/[,;\n]/).map(ref => 
      ref.trim().replace(/^[•\-\*]\s*/, '') // Remove leading bullets, dashes, asterisks
    ).filter(ref => ref.length > 0);
    // Translate each reference to the current language
    return refs.map(ref => translateScriptureReference(ref, language));
  };

  const extractDateFromTitle = (title: string): { month: string; year: string } | null => {
    // Russian months
    const russianMonths: Record<string, string> = {
      'января': '01', 'февраля': '02', 'марта': '03', 'апреля': '04',
      'мая': '05', 'июня': '06', 'июля': '07', 'августа': '08',
      'сентября': '09', 'октября': '10', 'ноября': '11', 'декабря': '12'
    };
    
    // English months
    const englishMonths: Record<string, string> = {
      'january': '01', 'february': '02', 'march': '03', 'april': '04',
      'may': '05', 'june': '06', 'july': '07', 'august': '08',
      'september': '09', 'october': '10', 'november': '11', 'december': '12'
    };

    // Match patterns like "23 Октября:" or "23 October:" (case insensitive)
    const match = title.match(/(\d{1,2})\s+([а-яА-Яa-zA-Z]+):/i);
    if (match) {
      const monthName = match[2].toLowerCase();
      const monthNum = russianMonths[monthName] || englishMonths[monthName];
      
      if (monthNum) {
        // Get current year or extract from title if present
        const yearMatch = title.match(/20\d{2}/);
        const year = yearMatch ? yearMatch[0] : new Date().getFullYear().toString();
        return { month: monthNum, year };
      }
    }
    
    return null;
  };

  const getMonthName = (month: string, year: string): string => {
    const monthNames = {
      '01': 'January', '02': 'February', '03': 'March', '04': 'April',
      '05': 'May', '06': 'June', '07': 'July', '08': 'August',
      '09': 'September', '10': 'October', '11': 'November', '12': 'December'
    };
    return `${monthNames[month as keyof typeof monthNames]} ${year}`;
  };

  const groupedVideos = videos.reduce((acc, video) => {
    const dateInfo = extractDateFromTitle(video.title);
    if (dateInfo) {
      const key = `${dateInfo.year}-${dateInfo.month}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(video);
    } else {
      if (!acc['no-date']) {
        acc['no-date'] = [];
      }
      acc['no-date'].push(video);
    }
    return acc;
  }, {} as Record<string, YouTubeVideo[]>);

  const sortedMonthKeys = Object.keys(groupedVideos)
    .filter(key => key !== 'no-date')
    .sort((a, b) => b.localeCompare(a)); // Sort descending (newest first)

  if (groupedVideos['no-date']) {
    sortedMonthKeys.push('no-date');
  }

  const availableMonths = sortedMonthKeys.map(key => {
    if (key === 'no-date') return { value: key, label: 'No Date' };
    const [year, month] = key.split('-');
    return { value: key, label: getMonthName(month, year) };
  });

  const filteredVideos = selectedMonth === 'all' 
    ? videos 
    : groupedVideos[selectedMonth] || [];

  if (loading) {
    return <div className="container mx-auto py-8">{t("youtube.admin.loading")}</div>;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t("youtube.admin.title")}</h1>
          <p className="text-muted-foreground">{t("youtube.admin.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              {t("youtube.admin.addvideo")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingVideo ? t("youtube.admin.editvideo") : t("youtube.admin.addnewvideo")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">{t("youtube.admin.form.title")}</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="youtube_url">{t("youtube.admin.form.url")}</Label>
                <Input
                  id="youtube_url"
                  type="url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">{t("youtube.admin.form.description")}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  placeholder="John 3:16, Romans 8:28"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Scripture references only (e.g., "John 3:16, Psalm 23"). Automatically translated to all languages.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="published"
                  checked={formData.published}
                  onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
                />
                <Label htmlFor="published">{t("youtube.admin.form.published")}</Label>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t("youtube.admin.form.cancel")}
                </Button>
                <Button type="submit">
                  {editingVideo ? t("youtube.admin.form.update") : t("youtube.admin.form.add")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6 flex items-center gap-3">
        <Calendar className="w-5 h-5 text-muted-foreground" />
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Filter by month" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Videos</SelectItem>
            {availableMonths.map(month => (
              <SelectItem key={month.value} value={month.value}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedMonth === 'all' ? (
        <Accordion type="single" collapsible className="space-y-4">
          {sortedMonthKeys.map(monthKey => {
            const monthVideos = groupedVideos[monthKey];
            const monthLabel = monthKey === 'no-date' 
              ? 'No Date' 
              : getMonthName(monthKey.split('-')[1], monthKey.split('-')[0]);
            
            return (
              <AccordionItem key={monthKey} value={monthKey} className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{monthLabel}</span>
                    <span className="text-sm text-muted-foreground">
                      ({monthVideos.length} {monthVideos.length === 1 ? 'video' : 'videos'})
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    {monthVideos.map((video) => {
                      const content = getVideoContent(video);
                      return (
                        <Card key={video.id}>
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <CardTitle>{content.title}</CardTitle>
                                <div className="mt-2 flex items-center gap-4 text-sm">
                                  <span className={video.published ? "text-green-600" : "text-yellow-600"}>
                                    {video.published ? t("youtube.admin.published") : t("youtube.admin.draft")}
                                  </span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button variant="outline" size="icon" onClick={() => handleEdit(video)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="icon" onClick={() => handleDelete(video.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {content.description && (
                              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                                {parseScriptureReferences(content.description).map((ref, idx) => (
                                  <BibleVerse key={idx} reference={ref} compact />
                                ))}
                              </div>
                            )}
                            <div className="aspect-video">
                              <iframe
                                width="100%"
                                height="100%"
                                src={`https://www.youtube.com/embed/${getYouTubeVideoId(video.youtube_url)}`}
                                title={content.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredVideos.map((video) => {
          const content = getVideoContent(video);
          return (
            <Card key={video.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle>{content.title}</CardTitle>
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      <span className={video.published ? "text-green-600" : "text-yellow-600"}>
                        {video.published ? t("youtube.admin.published") : t("youtube.admin.draft")}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => handleEdit(video)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => handleDelete(video.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.description && (
                  <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                    {parseScriptureReferences(content.description).map((ref, idx) => (
                      <BibleVerse key={idx} reference={ref} compact />
                    ))}
                  </div>
                )}
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(video.youtube_url)}`}
                    title={content.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filteredVideos.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              {selectedMonth === 'all' ? t("youtube.admin.novideos") : "No videos for this month"}
            </CardContent>
          </Card>
        )}
      </div>
      )}
    </div>
  );
}
